/**
 * LES ENTREES DU JOUEUR — ce que le client envoie a la place de sa position.
 *
 * Jusqu'ici chaque navigateur annoncait ou il se trouvait et le serveur le
 * croyait: un client modifie pouvait se teleporter, traverser un mur, ou
 * marcher a trente metres par seconde. Ce qui monte maintenant, c'est
 * l'INTENTION — la direction visee et la vitesse voulue pendant un tick — avec
 * un numero de sequence. Le serveur la rejoue sur SON terrain et devient la
 * verite sur les positions.
 *
 * TROIS CHOSES A COMPRENDRE, et tout le reste en decoule.
 *
 * 1. LE CLIENT APPLIQUE TOUT DE SUITE (prediction). Attendre la reponse du
 *    serveur, ce serait trente millisecondes de retard entre le doigt et le
 *    bonhomme, et le jeu deviendrait injouable sur un wifi de maison.
 *
 * 2. LE SERVEUR REPOND AVEC LA POSITION CONFIRMEE ET LE DERNIER NUMERO PRIS EN
 *    COMPTE. Le client repart de cette position et REJOUE par-dessus les
 *    entrees encore en vol (reconciliation). Si l'ecart avec ce qu'il affichait
 *    est minime — c'est le cas courant — il le resorbe en douceur au lieu de
 *    sauter: une correction qui se voit est pire que l'erreur qu'elle corrige.
 *
 * 3. TOUT REPOSE SUR LE FAIT QUE LES DEUX COTES CALCULENT EXACTEMENT LA MEME
 *    CHOSE. C'est pour ca que `appliquerEntree` est ici, dans du code pur, et
 *    qu'elle est appelee telle quelle par le navigateur ET par le serveur: un
 *    `dt` different, un ordre d'operations different, une collision de mur
 *    resolue d'un seul cote, et le joueur sent une secousse permanente. Les
 *    nombres voyagent en JSON, donc en doubles exacts: aucune quantification
 *    ne s'intercale entre ce que le client applique et ce que le serveur relit.
 */
import { PLAYER_RADIUS, RUN_SPEED, TICK_DT } from '../constants.ts'
import type { Batiment } from '../types/monde.ts'
import type { Joueur } from '../types/player.ts'
import type { ITerrain, Vec2 } from '../types/world.ts'
import { clamp, dist } from './alea.ts'
import { NIVEAU_MAX, niveauEffectif, resoudreMurs } from './batiments.ts'
import { formeDe } from './besoins.ts'
import { resoudreNoeuds, type NoeudsSolides } from './ressources.ts'
import {
  appliquerIntention,
  debloquer,
  intentionDe,
  verrouille,
  type GrilleNavigation,
  type OptionsDeplacement,
} from './joueur.ts'

/**
 * Une entree: un tick de volonte du joueur.
 *
 * Elle ne contient AUCUNE position. C'est la propriete qui fait tout: un client
 * ne peut plus annoncer ou il est, seulement ce qu'il essaie de faire, et le
 * resultat appartient au serveur.
 */
export interface Entree {
  /** Numero de sequence, croissant, jamais reutilise dans une partie. */
  n: number
  /** Duree du pas en secondes. Toujours TICK_DT; le serveur la borne quand meme. */
  dt: number
  /** Direction visee, brute et non normalisee (voir `Intention`). */
  dx: number
  dz: number
  /** Vitesse voulue en m/s, avant les modificateurs. Plafonnee a la course. */
  v: number
  /**
   * LA FORME du joueur, de 0 (a plat) a 1 (repose et rassasie).
   *
   * Elle voyage parce que le serveur NE TIENT PAS les besoins: il les recevrait
   * de toute facon du client. La borner a [0, 1] suffit — un menteur ne gagne
   * que le droit de se dire en pleine forme, c'est-a-dire de ne pas etre
   * ralenti quand il a faim et de nager au plafond du repose (SWIM_SPEED_MAX,
   * 2,8 m/s). Ca ne prend rien a personne, alors qu'un facteur non borne lui
   * donnerait la vitesse qu'il veut.
   *
   * ELLE PORTAIT AUTREFOIS LE FACTEUR DE RALENTISSEMENT (RALENTI_MIN a 1) et
   * porte maintenant la forme brute. La difference n'est pas cosmetique: le
   * facteur valait 1 pour TOUTE energie au-dessus de SEUIL_BAS, donc le serveur
   * ne pouvait pas distinguer un nageur epuise-mais-pas-encore-ralenti d'un
   * nageur frais, et aucune regle ne pouvait recompenser la forme. Les deux
   * lectures se font dans le pas partage: `facteurVitesseForme` (le
   * ralentissement, inchange) et `vitesseNage` (le plafond du nageur).
   *
   * C'est pour ce changement-la que VERSION_PROTOCOLE est passe de 3 a 4: la
   * FORME du fil n'a pas bouge d'un octet (toujours CHAMPS_ENTREE nombres),
   * seul le SENS de `f` a change. Un onglet reste ouvert sur l'ancien bundle
   * enverrait 0,55 pour dire « je suis ralenti » la ou le serveur lirait
   * « forme 0,55 », donc aucun ralentissement: les deux cotes calculeraient des
   * positions differentes pour un joueur affame. Un numero de version qu'on ne
   * bouge pas parce que « la structure est la meme » laisse passer exactement
   * ce genre de divergence.
   */
  f: number
  /**
   * L'ETAGE VOULU: 0 au sol, 1 sur la terrasse d'un batiment a etage.
   *
   * IL DOIT VOYAGER, et ce n'est pas un detail de confort. Le moteur n'a aucune
   * verticalite: la seule chose qui distingue « je marche dans le salon » de
   * « je marche sur le toit », a (x, z) identiques, c'est ce nombre. Sans lui,
   * le serveur resoudrait le joueur contre les murs du rez-de-chaussee pendant
   * qu'il fait le tour de sa terrasse, le repousserait a chaque tick, et le
   * navigateur passerait la soiree a recaler — la secousse permanente que tout
   * ce fichier existe pour eviter.
   *
   * Ce que le client peut en faire: `decoderEntrees` le borne a [0, NIVEAU_MAX]
   * et `appliquerEntree` le repasse par `niveauEffectif`, qui le ramene a zero
   * partout ou il n'y a pas d'etage. Un menteur ne gagne donc que le droit de se
   * tenir sur son propre toit sans avoir pris l'escalier.
   */
  niv: number
}

/** Nombre d'entiers par entree sur le fil. Meme forme que la faune. */
export const CHAMPS_ENTREE = 7

/**
 * Nombre maximum d'entrees dans un lot recu.
 *
 * A vingt entrees par seconde et un envoi toutes les 120 ms, un lot honnete en
 * porte trois. Le plafond laisse de la marge pour une image qui a rame, et
 * refuse le megaoctet envoye expres depuis une console de navigateur.
 */
export const MAX_ENTREES_LOT = 64

/**
 * Nombre d'entrees gardees en attente de confirmation.
 *
 * Dix secondes de jeu. Au-dela, ce n'est plus du retard reseau mais un serveur
 * qui ne repond pas: on oublie les plus vieilles plutot que de laisser un
 * tableau grossir toute la soiree.
 */
export const MAX_EN_VOL = 200

/**
 * Plafond du credit de temps du serveur, en secondes: le meme horizon que
 * MAX_EN_VOL, et ce n'est pas un hasard.
 *
 * Il valait 0,5 s, et c'etait LE bogue des teleportations: sur un wifi qui
 * hoquette, cinq secondes d'entrees arrivent d'un coup, le budget n'en
 * rejouait qu'une demi-seconde et jetait le reste EN AVANCANT quand meme le
 * numero confirme. Les quatre secondes et demie de marche jetees n'existaient
 * plus nulle part: le client, qui ne rejoue que ce qui n'est pas confirme,
 * sautait en arriere de vingt metres — toujours au moment ou l'on s'arrete
 * pour couper un arbre, parce que c'est la que la rafale rattrape.
 *
 * A dix secondes, le serveur rejoue tout ce que le client est encore capable
 * de rejouer de son cote (MAX_EN_VOL entrees de TICK_DT): les deux memoires
 * ont le meme horizon, donc une rafale honnete ne perd plus rien. Ce qu'un
 * tricheur y gagne est borne et assume: se taire dix secondes puis les
 * depenser d'un coup, soit 70 m de course VALIDE (chaque entree passe par les
 * murs et le terrain) — jamais une vitesse moyenne au-dessus de la course,
 * puisque le credit reste le temps reellement ecoule.
 */
export const PLAFOND_CREDIT = MAX_EN_VOL * TICK_DT

/** Sous cet ecart, on ne corrige rien: c'est le bruit normal du reseau. */
export const SEUIL_IGNORE = 0.03

/**
 * Au-dela de cet ecart, on saute au lieu de lisser.
 *
 * Deux metres et demi, c'est plus qu'un pas de course perdu et moins qu'une
 * piece de maison: en dessous on est dans l'imprecision, au-dessus le client
 * s'est vraiment trompe d'endroit et le lissage mettrait des secondes a
 * l'amener a bon port, en traversant ce qui se trouve entre les deux.
 */
export const SEUIL_SAUT = 2.5

/**
 * Part de l'ecart resorbee a chaque tick, quand on corrige en douceur.
 *
 * A 20 Hz, un cinquieme par tick fait fondre une erreur de 50 cm en un quart de
 * seconde, sans qu'on voie jamais le bonhomme se deplacer tout seul. Meme
 * principe que le rattrapage de l'horloge (client/horloge-suivie.ts): on ne
 * pose pas la valeur du serveur, on va vers elle.
 */
export const TAUX_RECALAGE = 0.2

// ------------------------------------------------------------------ un tick

/**
 * L'entree du tick, deduite de l'etat du joueur.
 *
 * Rend une entree meme quand il n'y a rien a faire (`v` a zero): la SEQUENCE ne
 * doit pas avoir de trous, sinon le serveur ne saurait pas distinguer un joueur
 * immobile d'un joueur dont les paquets se perdent.
 */
export function entreeDeTick(j: Joueur, n: number, dt = TICK_DT): Entree {
  // L'etage part MEME quand le joueur ne bouge pas: c'est un etat, pas un
  // mouvement. Une entree immobile qui l'oublierait ramenerait le serveur au
  // rez-de-chaussee des qu'on s'arrete sur sa terrasse.
  const niv = j.niveau
  if (verrouille(j)) return { n, dt, dx: 0, dz: 0, v: 0, f: 1, niv }
  const intention = intentionDe(j)
  if (!intention) return { n, dt, dx: 0, dz: 0, v: 0, f: 1, niv }
  return { n, dt, dx: intention.dx, dz: intention.dz, v: intention.vitesse, f: formeDe(j.besoins), niv }
}

/**
 * UN TICK D'AUTORITE: la seule fonction qui deplace un joueur, des deux cotes.
 *
 * L'ordre des trois etapes est le contrat: intention, puis murs, puis
 * deblocage. Le changer d'un cote seulement suffirait a faire diverger le
 * navigateur du serveur, et c'est precisement ce qu'on cherche a rendre
 * impossible en n'ecrivant la sequence qu'une fois.
 *
 * Rend l'endroit ou le joueur a ete degage, ou `null` s'il ne l'a pas ete:
 * l'appelant en tire ce qu'il veut (un message a l'ecran, une ligne de journal).
 */
export function appliquerEntree(
  j: Joueur,
  e: Entree,
  terrain: ITerrain,
  grille: GrilleNavigation,
  batiments: readonly Batiment[],
  options?: OptionsDeplacement,
  noeuds?: NoeudsSolides,
): Vec2 | null {
  /*
   * L'ETAGE D'ABORD, avant tout le reste, et a partir de la position de DEPART.
   *
   * C'est lui qui decide contre quels murs le pas va se resoudre: le fixer en
   * cours de route, ou apres le deplacement, reviendrait a l'evaluer sur deux
   * positions differentes de chaque cote du fil. Il est deduit du seul nombre
   * qui voyage (`e.niv`) et de la liste des batiments, que les deux cotes ont a
   * l'identique — donc le navigateur et le serveur tombent sur le meme etage.
   */
  j.niveau = niveauEffectif(batiments, j.pos.x, j.pos.z, e.niv)

  // L'origine du pas, gardee AVANT de bouger: c'est elle qui dit au filet
  // anti-blocage si le bonhomme vient de descendre un flanc en toute legalite
  // (voir `monteeAcceptable` dans shared/sim/joueur.ts).
  const depart = { x: j.pos.x, z: j.pos.z }

  if (!verrouille(j)) {
    appliquerIntention(j, { dx: e.dx, dz: e.dz, vitesse: e.v }, e.dt, terrain, grille, e.f, options)
  }

  // Les murs des maisons ne sont pas dans la grille de navigation (on y entre):
  // ils se resolvent ici, ce qui laisse la porte franchissable. A l'etage, ce
  // sont l'acrotere et le bureau qui repoussent, jamais le rez-de-chaussee.
  const corrige = resoudreMurs(batiments, j.pos, j.niveau)
  j.pos.x = corrige.x
  j.pos.z = corrige.z

  /*
   * LES TRONCS ET LES ROCHERS, au sol et a pied seulement (une terrasse n'a
   * pas d'arbre, et a bord c'est le vehicule qui decide). Murs et noeuds
   * ALTERNENT deux fois: un rocher colle a un mur repoussait le bonhomme dans
   * le mur, qui le renvoyait au tick suivant contre le rocher — la navette des
   * deux bandes de murs qui se touchent, deja payee a l'etage et au zoo.
   */
  if (noeuds && j.niveau === 0 && !options?.aBord) {
    for (let passe = 0; passe < 2; passe++) {
      const hors = resoudreNoeuds(noeuds, j.pos, PLAYER_RADIUS)
      if (!hors) break
      j.pos.x = hors.x
      j.pos.z = hors.z
      const murs = resoudreMurs(batiments, j.pos, j.niveau)
      j.pos.x = murs.x
      j.pos.z = murs.z
    }
  }

  const libre = debloquer(grille, terrain, batiments, j.pos.x, j.pos.z, j.niveau, depart)
  if (libre) {
    j.pos.x = libre.x
    j.pos.z = libre.z
    j.chemin.length = 0
    j.direction = null
  }
  return libre
}

// ------------------------------------------------------------------ le fil

/** Met un lot d'entrees en entiers, dans l'ordre d'emission. */
export function encoderEntrees(entrees: readonly Entree[]): number[] {
  const sortie: number[] = []
  for (const e of entrees) sortie.push(e.n, e.dt, e.dx, e.dz, e.v, e.f, e.niv)
  return sortie
}

/**
 * Relit un lot recu, en BORNANT chaque champ.
 *
 * C'est le seul endroit ou le serveur regarde ce qu'un client raconte de son
 * deplacement, donc c'est ici que se refuse l'impossible: un pas plus long
 * qu'un tick, une vitesse superieure a la course, un facteur de forme
 * au-dessus de 1, une direction qui n'est pas un nombre. Rien n'est rejete —
 * tout est ramene dans les clous, et le joueur avance simplement moins vite
 * qu'il ne l'esperait.
 */
export function decoderEntrees(bruts: readonly number[]): Entree[] | null {
  if (bruts.length === 0 || bruts.length % CHAMPS_ENTREE !== 0) return null
  if (bruts.length / CHAMPS_ENTREE > MAX_ENTREES_LOT) return null
  const sortie: Entree[] = []
  for (let k = 0; k < bruts.length; k += CHAMPS_ENTREE) {
    const n = bruts[k] as number
    const dt = bruts[k + 1] as number
    const dx = bruts[k + 2] as number
    const dz = bruts[k + 3] as number
    const v = bruts[k + 4] as number
    const f = bruts[k + 5] as number
    const niv = bruts[k + 6] as number
    // Un seul NaN dans le lot et tout le lot est jete: recoller les suivants
    // decalerait les champs et ferait avancer le joueur n'importe ou.
    if (![n, dt, dx, dz, v, f, niv].every((x) => Number.isFinite(x))) return null
    if (n < 0 || n > Number.MAX_SAFE_INTEGER) return null
    sortie.push({
      n: Math.floor(n),
      dt: clamp(dt, 0, TICK_DT),
      // Les directions sont des ecarts en metres, jamais des coordonnees: un
      // monde fait 500 m, dix mille est deja de l'absurde borne.
      dx: clamp(dx, -1e4, 1e4),
      dz: clamp(dz, -1e4, 1e4),
      v: clamp(v, 0, RUN_SPEED),
      f: clamp(f, 0, 1),
      // Un etage est un ENTIER dans une echelle fermee: 2,7 n'a pas de sens, et
      // 900 non plus. On plancherise avant de borner, sinon 0,9 monterait a 1.
      niv: clamp(Math.floor(niv), 0, NIVEAU_MAX),
    })
  }
  return sortie
}

// ---------------------------------------------------------------- le budget

/**
 * LE BUDGET DE TEMPS: ce qui empeche de courir en envoyant plus d'entrees.
 *
 * Borner la duree d'une entree ne suffit pas — il reste a en envoyer mille par
 * seconde. Le serveur n'accorde donc que le temps qui s'est REELLEMENT ecoule:
 * une seconde de vie du monde donne droit a une seconde de deplacement, pas
 * davantage. C'est la borne qui rend le reste des verifications inutile a
 * contourner.
 *
 * Pur: l'heure arrive en argument, comme partout dans shared/.
 */
export class BudgetEntrees {
  /** Credit disponible, en secondes de simulation. */
  private credit: number
  private dernier: number
  /** Entrees refusees faute de credit, pour le journal. */
  private refusees = 0

  /**
   * `avance` est le credit de depart, en secondes: il absorbe la rafale d'un
   * client qui vient d'ouvrir sa liaison et rattrape ses premieres images.
   */
  constructor(maintenantMs: number, avance = 0.3) {
    this.dernier = maintenantMs
    this.credit = avance
  }

  /**
   * Consomme le temps demande, ou refuse.
   *
   * Le credit est PLAFONNE (PLAFOND_CREDIT): un joueur qui reste une minute
   * sans rien envoyer (onglet en arriere-plan, wifi coupe) ne doit pas revenir
   * avec une minute de course en reserve — mais le plafond doit couvrir la
   * rafale d'un wifi qui hoquette, sans quoi c'est l'honnete qu'on teleporte
   * en arriere (voir PLAFOND_CREDIT).
   */
  accorder(maintenantMs: number, dt: number, plafond = PLAFOND_CREDIT): boolean {
    const ecoule = Math.max(0, maintenantMs - this.dernier) / 1000
    this.dernier = maintenantMs
    this.credit = Math.min(plafond, this.credit + ecoule)
    if (this.credit < dt) {
      this.refusees++
      return false
    }
    this.credit -= dt
    return true
  }

  /** Nombre d'entrees refusees depuis le debut. Sert au journal et aux tests. */
  get refus(): number {
    return this.refusees
  }
}

// -------------------------------------------------------- la reconciliation

/** Ce qu'une reponse du serveur a change chez le client. */
export interface ResultatRecalage {
  /** Distance entre ce que le client affichait et ce que le serveur impose. */
  ecart: number
  /** Vrai si l'ecart etait trop grand pour etre lisse: on a saute. */
  saut: boolean
  /** Nombre d'entrees encore en vol, rejouees par-dessus la position recue. */
  rejouees: number
}

/**
 * Le cote client de l'autorite: garder ses entrees, les rejouer, corriger.
 *
 * Classe et non fonctions parce que tout tient dans une memoire: les entrees
 * pas encore confirmees, celles pas encore emises, et l'ecart en cours de
 * resorption. Pure, donc testable sans navigateur — et c'est la meilleure
 * facon de tester une reconciliation: rejouer une suite d'entrees des deux
 * cotes et exiger le meme etat final.
 */
export class Reconciliation {
  /** Entrees appliquees chez soi et pas encore confirmees par le serveur. */
  private enVol: Entree[] = []
  /** Entrees pas encore parties sur le fil. */
  private aEmettre: Entree[] = []
  /** Ecart restant a resorber en douceur, en metres. */
  private corrX = 0
  private corrZ = 0

  /** Note une entree qu'on vient d'appliquer chez soi. */
  noter(e: Entree): void {
    this.enVol.push(e)
    this.aEmettre.push(e)
    // Un serveur muet ne doit pas faire grossir la memoire du navigateur.
    if (this.enVol.length > MAX_EN_VOL) this.enVol.splice(0, this.enVol.length - MAX_EN_VOL)
  }

  /** Le lot a envoyer, encode. Vide si rien n'attend. */
  lot(): number[] {
    if (this.aEmettre.length === 0) return []
    // On n'envoie jamais plus qu'un lot recevable: au-dela, le serveur jetterait
    // tout, et c'est le joueur qui rame qui serait puni.
    if (this.aEmettre.length > MAX_ENTREES_LOT) {
      this.aEmettre.splice(0, this.aEmettre.length - MAX_ENTREES_LOT)
    }
    const nombres = encoderEntrees(this.aEmettre)
    this.aEmettre.length = 0
    return nombres
  }

  /** Nombre d'entrees en attente de confirmation. Sert aux tests et au journal. */
  get enAttente(): number {
    return this.enVol.length
  }

  /**
   * Applique une position confirmee par le serveur.
   *
   * `j` est le joueur du client, deja predit: on repart de la position du
   * serveur, on rejoue par-dessus ce qui etait encore en vol, et on compare au
   * resultat qu'il affichait. Le joueur n'est deplace que du strict necessaire.
   */
  recevoir(
    n: number,
    x: number,
    z: number,
    rot: number,
    j: Joueur,
    terrain: ITerrain,
    grille: GrilleNavigation,
    batiments: readonly Batiment[],
    options?: OptionsDeplacement,
    noeuds?: NoeudsSolides,
  ): ResultatRecalage {
    // Une reponse en retard (recue apres une plus recente) n'apprend rien:
    // l'appliquer ferait reculer le bonhomme d'un aller-retour reseau.
    this.enVol = this.enVol.filter((e) => e.n > n)

    // Le fantome: meme etat que le joueur, mais parti de la position du
    // serveur. On ne touche au vrai joueur qu'a la toute fin.
    const fantome: Joueur = {
      ...j,
      pos: { x, z },
      rot,
      // Le chemin et la manette sont des generateurs d'intention: les entrees
      // rejouees portent deja ce qu'ils avaient produit, les relire ferait le
      // travail deux fois.
      chemin: [],
      direction: null,
      /*
       * LE FANTOME N'EST JAMAIS VERROUILLE, comme la copie du serveur. Il
       * recopiait l'etat COURANT du joueur: des qu'on s'arretait pour couper un
       * arbre (`recolte`), les derniers pas encore en vol etaient rejoues
       * « verrouilles », donc ignores, et le bonhomme etait tire en arriere —
       * puis en avant au recalage suivant. Une entree emise pendant un geste
       * vaut deja zero (`entreeDeTick`): il n'y a rien a verrouiller ici.
       */
      etat: 'immobile',
    }
    for (const e of this.enVol) appliquerEntree(fantome, e, terrain, grille, batiments, options, noeuds)

    const ecart = dist(j.pos.x, j.pos.z, fantome.pos.x, fantome.pos.z)
    const rejouees = this.enVol.length

    if (ecart < SEUIL_IGNORE) {
      // Le cas courant, et le seul qui compte pour la sensation de jeu: le
      // client avait raison, il n'y a rien a faire.
      this.corrX = 0
      this.corrZ = 0
      return { ecart, saut: false, rejouees }
    }

    if (ecart >= SEUIL_SAUT) {
      j.pos.x = fantome.pos.x
      j.pos.z = fantome.pos.z
      j.rot = fantome.rot
      j.chemin.length = 0
      j.direction = null
      this.corrX = 0
      this.corrZ = 0
      return { ecart, saut: true, rejouees }
    }

    this.corrX = fantome.pos.x - j.pos.x
    this.corrZ = fantome.pos.z - j.pos.z
    return { ecart, saut: false, rejouees }
  }

  /**
   * Resorbe une part de l'ecart. A appeler une fois par tick, apres l'entree.
   *
   * Le bonhomme n'est jamais POSE sur la position du serveur: il y glisse. Une
   * correction qu'on voit est une correction ratee.
   */
  lisser(j: Joueur): void {
    if (this.corrX === 0 && this.corrZ === 0) return
    const px = this.corrX * TAUX_RECALAGE
    const pz = this.corrZ * TAUX_RECALAGE
    j.pos.x += px
    j.pos.z += pz
    this.corrX -= px
    this.corrZ -= pz
    if (Math.hypot(this.corrX, this.corrZ) < 1e-3) {
      this.corrX = 0
      this.corrZ = 0
    }
  }

  /** Ecart restant a resorber, en metres. Sert aux tests. */
  get reste(): number {
    return Math.hypot(this.corrX, this.corrZ)
  }

  /**
   * Oublie tout.
   *
   * A la coupure de la liaison, et a la montee dans un vehicule: dans les deux
   * cas le serveur cesse d'etre l'autorite, et garder des entrees en vol ferait
   * rejouer, au retour, un deplacement qui a deja eu lieu autrement.
   */
  vider(): void {
    this.enVol.length = 0
    this.aEmettre.length = 0
    this.corrX = 0
    this.corrZ = 0
  }
}
